// ============================================
// Auth Redux Slice — Role-Aware Token Storage
// ============================================

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../../services/api';
import { API_BASE_URL } from '../../constants';

// ── Helper: get token key prefix based on current URL ──
const getRolePrefix = () => {
    const path = window.location.pathname;
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/dispatcher')) return 'dispatcher';
    if (path.startsWith('/driver')) return 'driver';
    if (path.startsWith('/pa')) return 'pa';
    return ''; // default for user
};

const getTokenKey = (prefix) => prefix ? `${prefix}_token` : 'token';
const getRefreshKey = (prefix) => prefix ? `${prefix}_refreshToken` : 'refreshToken';

// ── Async Thunks ────────────────────────────

export const loginUser = createAsyncThunk(
    'auth/login',
    async ({ email, password, loginRole }, { rejectWithValue }) => {
        try {
            const { data } = await api.post('/auth/login', { email, password });
            const user = data.data.user;

            // Strict Role Validation
            if (loginRole) {
                const expectedRole = loginRole === 'user' ? ['user', 'passenger'] : [loginRole];
                if (!expectedRole.includes(user.role)) {
                    const actualRoleDisplay = user.role === 'pa' ? 'PA' : user.role.charAt(0).toUpperCase() + user.role.slice(1);
                    return rejectWithValue(`This account is already registered as a ${actualRoleDisplay}. Please log in via the ${actualRoleDisplay} portal.`);
                }
            }

            // Store tokens under role-specific keys
            // 'user' role uses no prefix (matches getTokenForCurrentPath default)
            const prefix = (loginRole && loginRole !== 'user') ? loginRole : getRolePrefix();
            localStorage.setItem(getTokenKey(prefix), data.data.token);
            localStorage.setItem(getRefreshKey(prefix), data.data.refreshToken);

            return data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Login failed'
            );
        }
    }
);

export const googleLogin = createAsyncThunk(
    'auth/googleLogin',
    async ({ firebaseToken, role }, { rejectWithValue }) => {
        try {
            const { data } = await api.post('/auth/google-login', { firebaseToken, role });
            const user = data.data.user;

            // Strict Role Validation
            if (role) {
                const expectedRole = role === 'user' ? ['user', 'passenger'] : [role];
                if (!expectedRole.includes(user.role)) {
                    const actualRoleDisplay = user.role === 'pa' ? 'PA' : user.role.charAt(0).toUpperCase() + user.role.slice(1);
                    return rejectWithValue(`This account is already registered as a ${actualRoleDisplay}. Please log in via the ${actualRoleDisplay} portal.`);
                }
            }

            const prefix = (role === 'driver' || role === 'pa') ? role : '';
            localStorage.setItem(getTokenKey(prefix), data.data.token);
            localStorage.setItem(getRefreshKey(prefix), data.data.refreshToken);

            return data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Google Login failed'
            );
        }
    }
);

export const registerUser = createAsyncThunk(
    'auth/register',
    async (userData, { rejectWithValue }) => {
        try {
            const { data } = await api.post('/auth/register', userData);

            // Return data without auto-login state changes
            return data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Registration failed'
            );
        }
    }
);

// Removed verifyOtp createAsyncThunk as we verify via Firebase frontend directly now

export const loadUser = createAsyncThunk(
    'auth/loadUser',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await api.get('/auth/profile');
            return data.data;
        } catch (error) {
            // Clear tokens for the current role if profile fetch fails
            const prefix = getRolePrefix();
            localStorage.removeItem(getTokenKey(prefix));
            localStorage.removeItem(getRefreshKey(prefix));
            return rejectWithValue(
                error.response?.data?.message || 'Failed to load user'
            );
        }
    }
);

export const logoutUser = createAsyncThunk(
    'auth/logout',
    async (_, { rejectWithValue }) => {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            // Proceed with clearing tokens even if API fails
        }

        // Clear tokens for the current role after API call
        const prefix = getRolePrefix();
        localStorage.removeItem(getTokenKey(prefix));
        localStorage.removeItem(getRefreshKey(prefix));
        localStorage.removeItem('user'); 
    }
);

// ── Startup Refresh Thunk ─────────────────────
// Called on page load when access token is missing but refresh token exists.
// This properly handles Step 68 (delete access token only → auto-restore).
export const tryRefreshOnStartup = createAsyncThunk(
    'auth/tryRefreshOnStartup',
    async (_, { rejectWithValue }) => {
        const prefix = getRolePrefix();
        const refreshToken = localStorage.getItem(getRefreshKey(prefix));
        if (!refreshToken) return rejectWithValue('No refresh token');
        try {
            const { data: refreshData } = await axios.post(
                `${API_BASE_URL}/auth/refresh`,
                { refreshToken }
            );
            const newToken = refreshData.data.token;
            localStorage.setItem(getTokenKey(prefix), newToken);
            // Fetch profile with the new access token
            const { data: profileData } = await axios.get(
                `${API_BASE_URL}/auth/profile`,
                { headers: { Authorization: `Bearer ${newToken}` } }
            );
            return { token: newToken, user: profileData.data.user };
        } catch (error) {
            // Refresh token is also invalid — clear it so ProtectedRoute redirects
            localStorage.removeItem(getRefreshKey(prefix));
            localStorage.removeItem('user');
            return rejectWithValue('Session expired — please log in again');
        }
    }
);

// ── Slice ────────────────────────────────────

// Determine initial token from current URL path.
// IMPORTANT: Each portal is strictly isolated — no cross-portal fallback.
// If admin_token is gone, we ONLY check admin_refreshToken.
// We never fall back to driver_token/pa_token/dispatcher_token.
const currentPrefix = getRolePrefix();
const currentToken = localStorage.getItem(getTokenKey(currentPrefix));
const currentRefreshToken = localStorage.getItem(getRefreshKey(currentPrefix));

const initialState = {
    user: null,
    token: currentToken,
    isAuthenticated: false,
    // isLoading = true if EITHER token exists (access or refresh).
    // This holds the loading spinner while we validate / attempt a startup refresh.
    isLoading: !!(currentToken || currentRefreshToken),
    error: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetAuth: (state) => {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            state.isLoading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Login
        builder
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.error = null;
                if (action.payload.user?.role) {
                    localStorage.setItem('last_role', action.payload.user.role);
                }
                localStorage.setItem('user', JSON.stringify(action.payload.user));
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Google Login
        builder
            .addCase(googleLogin.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(googleLogin.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.error = null;
                localStorage.setItem('user', JSON.stringify(action.payload.user));
            })
            .addCase(googleLogin.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Register
        builder
            .addCase(registerUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Verify OTP (Removed)

        // Load User
        builder
            .addCase(loadUser.pending, (state) => {
                // Only show loading spinner on initial auth check (first load).
                // If user is already set, this is a refresh (e.g. after updating profile),
                // so don't set isLoading to avoid unmounting ProtectedRoute children.
                if (!state.user) {
                    state.isLoading = true;
                }
            })
            .addCase(loadUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.user = action.payload.user;
                localStorage.setItem('user', JSON.stringify(action.payload.user));
            })
            .addCase(loadUser.rejected, (state) => {
                state.isLoading = false;
                state.isAuthenticated = false;
                state.user = null;
                state.token = null;
            });

        // Logout
        builder
            .addCase(logoutUser.pending, (state) => {
                state.user = null;
                state.token = null;
                state.isAuthenticated = false;
                state.isLoading = false;
            })
            .addCase(logoutUser.fulfilled, (state) => {
                state.user = null;
                state.token = null;
                state.isAuthenticated = false;
                state.isLoading = false;
            })
            .addCase(logoutUser.rejected, (state) => {
                state.user = null;
                state.token = null;
                state.isAuthenticated = false;
                state.isLoading = false;
            });

        // Startup Refresh (Step 68: access token deleted but refresh token exists)
        builder
            .addCase(tryRefreshOnStartup.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.token = action.payload.token;
                state.user = action.payload.user;
                localStorage.setItem('user', JSON.stringify(action.payload.user));
            })
            .addCase(tryRefreshOnStartup.rejected, (state) => {
                // Both tokens gone/invalid → force clean state, ProtectedRoute redirects
                state.isLoading = false;
                state.isAuthenticated = false;
                state.token = null;
                state.user = null;
            });
    },
});

export const { clearError, resetAuth } = authSlice.actions;
export default authSlice.reducer;
